import { X } from "lucide-react";
import { useCallback, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/components/ui/form";
import { Input } from "../../../ui/components/ui/input";
import { Textarea } from "../../../ui/components/ui/textarea";
import {
  COMPANY_CATEGORIES,
  CompanyCategory,
  DEFAULT_POSITION,
  EMPLOYMENT_TYPES,
  EmploymentType,
  JOB_TITLES_BY_CATEGORY,
  OnboardingFormData,
} from "../onboarding-schema";

interface PositionDataStepProps {
  form: UseFormReturn<OnboardingFormData>;
}

const PositionDataStep = ({ form }: PositionDataStepProps) => {
  const locations = form.watch("locations") ?? [];

  return (
    <div>
      <p className="text-sm font-medium mb-1" style={{ color: "#0099ce" }}>
        Schritt 6 von 9
      </p>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Welche Stellen sollen besetzt werden?</h1>
      <p className="text-sm text-gray-500 mb-6">
        Wählen Sie für jeden Standort die offenen Stellen aus und beschreiben Sie diese möglichst genau. Je
        detaillierter die Angaben, desto passender die Bewerber.
      </p>

      <div className="space-y-8">
        {locations.map((_, locIndex) => (
          <LocationPositionsCard key={locIndex} form={form} locIndex={locIndex} />
        ))}
      </div>
    </div>
  );
};

const LocationPositionsCard = ({ form, locIndex }: { form: UseFormReturn<OnboardingFormData>; locIndex: number }) => {
  const categories: CompanyCategory[] = form.watch(`locations.${locIndex}.categories`) ?? [];
  const positions = form.watch(`locations.${locIndex}.positions`) ?? [];
  const locationName = form.watch(`locations.${locIndex}.name`);

  const categoryLabels = categories
    .map((val) => COMPANY_CATEGORIES.find((c) => c.value === val)?.label)
    .filter(Boolean);

  const availableJobTitles = (() => {
    const titles = new Set<string>();
    for (const cat of categories) {
      const catTitles = JOB_TITLES_BY_CATEGORY[cat];
      if (catTitles) catTitles.forEach((t) => titles.add(t));
    }
    // Keep backend-prefilled positions visible/selectable even when categories are missing.
    positions.forEach((position) => {
      if (position.jobTitle) {
        titles.add(position.jobTitle);
      }
    });
    return Array.from(titles);
  })();

  const selectedJobTitles = new Set(positions.map((p) => p.jobTitle));

  const toggleJobTitle = useCallback(
    (title: string) => {
      const currentPositions = form.getValues(`locations.${locIndex}.positions`) ?? [];
      const existingIndex = currentPositions.findIndex((p) => p.jobTitle === title);

      if (existingIndex >= 0) {
        form.setValue(
          `locations.${locIndex}.positions`,
          currentPositions.filter((_, i) => i !== existingIndex),
          { shouldValidate: true },
        );
      } else {
        const globalBenefits = form.getValues("benefits") ?? [];
        form.setValue(
          `locations.${locIndex}.positions`,
          [...currentPositions, { ...DEFAULT_POSITION, jobTitle: title, benefits: [...globalBenefits] }],
          { shouldValidate: true },
        );
      }
    },
    [form, locIndex],
  );

  const positionsError = form.formState.errors.locations?.[locIndex]?.positions;
  const positionsRootError = positionsError?.root ?? positionsError;

  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
      <div className="mb-6">
        <p className="text-base font-semibold text-gray-900 mb-1">
          Standort {locIndex + 1}
          {locationName ? ` – ${locationName}` : ""}
        </p>
        {categoryLabels.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {categoryLabels.map((label) => (
              <span
                key={label}
                className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600"
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Job title multi-select */}
      <div className="mb-6">
        <p className="text-sm font-medium text-gray-700 mb-2">
          Offene Stellen <span className="text-red-500">*</span>
        </p>
        {availableJobTitles.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {availableJobTitles.map((title) => {
              const isSelected = selectedJobTitles.has(title);
              return (
                <button
                  key={title}
                  type="button"
                  onClick={() => toggleJobTitle(title)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${
                    isSelected
                      ? "text-white border-transparent shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                  }`}
                  style={{ backgroundColor: isSelected ? "#0099ce" : undefined }}
                >
                  {title}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">
            Keine Berufsbezeichnungen verfügbar. Bitte wählen Sie zuerst Branchen in Schritt 1.
          </p>
        )}
        {positionsRootError && "message" in positionsRootError && (
          <p className="text-sm font-medium text-destructive mt-2">{positionsRootError.message as string}</p>
        )}
      </div>

      {/* Position detail cards */}
      {positions.length > 0 && (
        <div className="space-y-6">
          {positions.map((pos, posIndex) => (
            <PositionDetailCard
              key={pos.jobTitle}
              form={form}
              locIndex={locIndex}
              posIndex={posIndex}
              jobTitle={pos.jobTitle}
              onRemove={() => toggleJobTitle(pos.jobTitle)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PositionDetailCard = ({
  form,
  locIndex,
  posIndex,
  jobTitle,
  onRemove,
}: {
  form: UseFormReturn<OnboardingFormData>;
  locIndex: number;
  posIndex: number;
  jobTitle: string;
  onRemove: () => void;
}) => {
  const [benefitInput, setBenefitInput] = useState("");
  const prefix = `locations.${locIndex}.positions.${posIndex}` as const;

  const selectedEmploymentTypes: string[] =
    form.watch(`locations.${locIndex}.positions.${posIndex}.belongsToWorkingTimeModels`) ?? [];
  const positionBenefits: string[] = form.watch(`locations.${locIndex}.positions.${posIndex}.benefits`) ?? [];
  const catchmentAreaKm: number = form.watch(`locations.${locIndex}.positions.${posIndex}.catchmentAreaKm`) ?? 25;

  const toggleEmploymentType = useCallback(
    (type: EmploymentType) => {
      const current = form.getValues(`locations.${locIndex}.positions.${posIndex}.belongsToWorkingTimeModels`) ?? [];
      const updated = current.includes(type) ? current.filter((t) => t !== type) : [...current, type];
      form.setValue(`locations.${locIndex}.positions.${posIndex}.belongsToWorkingTimeModels`, updated, {
        shouldValidate: true,
      });
    },
    [form, locIndex, posIndex],
  );

  const addPositionBenefit = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      const current = form.getValues(`locations.${locIndex}.positions.${posIndex}.benefits`) ?? [];
      if (current.includes(trimmed)) return;
      form.setValue(`locations.${locIndex}.positions.${posIndex}.benefits`, [...current, trimmed], {
        shouldValidate: true,
      });
    },
    [form, locIndex, posIndex],
  );

  const removePositionBenefit = useCallback(
    (value: string) => {
      const current = form.getValues(`locations.${locIndex}.positions.${posIndex}.benefits`) ?? [];
      form.setValue(
        `locations.${locIndex}.positions.${posIndex}.benefits`,
        current.filter((b) => b !== value),
        { shouldValidate: true },
      );
    },
    [form, locIndex, posIndex],
  );

  const handleBenefitAdd = () => {
    addPositionBenefit(benefitInput);
    setBenefitInput("");
  };

  const handleBenefitKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleBenefitAdd();
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-6 relative">
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-4 right-4 p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>

      <p className="text-sm font-semibold text-gray-900 mb-5">{jobTitle}</p>

      <div className="space-y-5">
        {/* Arbeitszeitmodell */}
        <FormField
          control={form.control}
          name={`${prefix}.belongsToWorkingTimeModels`}
          render={() => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Arbeitszeitmodell <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <div className="flex flex-wrap gap-2 mt-1">
                  {EMPLOYMENT_TYPES.map((et) => {
                    const isSelected = selectedEmploymentTypes.includes(et.value);
                    return (
                      <button
                        key={et.value}
                        type="button"
                        onClick={() => toggleEmploymentType(et.value)}
                        className={`rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${
                          isSelected
                            ? "text-white border-transparent shadow-sm"
                            : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                        }`}
                        style={{ backgroundColor: isSelected ? "#0099ce" : undefined }}
                      >
                        {et.label}
                      </button>
                    );
                  })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Stellenbeschreibung */}
        <FormField
          control={form.control}
          name={`${prefix}.description`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Stellenbeschreibung <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={3}
                  placeholder="Beschreiben Sie die Stelle und die Aufgaben möglichst genau."
                  className="mt-1 resize-y"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Einzugsgebiet / Radius */}
        <FormField
          control={form.control}
          name={`${prefix}.catchmentAreaKm`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Einzugsgebiet <span className="text-red-500">*</span>
              </FormLabel>
              <p className="text-xs text-gray-400 mb-1">In welchem Umkreis um den Standort suchen Sie Bewerber?</p>
              <FormControl>
                <div className="flex items-center gap-4 mt-1">
                  <input
                    type="range"
                    min={5}
                    max={100}
                    step={5}
                    value={catchmentAreaKm}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    className="flex-1 accent-[#0099ce]"
                  />
                  <span className="text-sm font-semibold text-gray-700 min-w-[56px] text-right">{catchmentAreaKm} km</span>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Benefits (per position) */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">Benefits</p>
          <p className="text-xs text-gray-400 mb-2">
            Übernommen aus Schritt 3. Sie können Benefits pro Stelle anpassen.
          </p>

          <div className="flex gap-2 mb-2">
            <Input
              value={benefitInput}
              onChange={(e) => setBenefitInput(e.target.value)}
              onKeyDown={handleBenefitKeyDown}
              placeholder="Benefit hinzufügen"
              className="flex-1"
            />
            <button
              type="button"
              onClick={handleBenefitAdd}
              disabled={!benefitInput.trim()}
              className="px-3 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#0099ce" }}
            >
              +
            </button>
          </div>

          {positionBenefits.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {positionBenefits.map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-white"
                  style={{ backgroundColor: "#0099ce" }}
                >
                  {b}
                  <button
                    type="button"
                    onClick={() => removePositionBenefit(b)}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Anforderungen & Qualifikationen */}
        <FormField
          control={form.control}
          name={`${prefix}.applicantProfile`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Anforderungen & Qualifikationen <span className="text-red-500">*</span>
              </FormLabel>
              <p className="text-xs text-gray-400 mb-1">
                Welche Qualifikationen, Erfahrungen oder Nachweise benötigen Bewerber?
              </p>
              <FormControl>
                <Textarea
                  {...field}
                  rows={3}
                  placeholder="z.B. Abgeschlossene Ausbildung als Pflegefachkraft, Berufserfahrung erwünscht"
                  className="mt-1 resize-y"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default PositionDataStep;
